// Redux.
import { connect } from "react-redux";
import { useEffect, useLayoutEffect, useRef } from "react";
import FeedRefreshNotice from './FeedRefreshNotice';
import {
    loadForMePreviews,
    applyPreparedForMe,
    prepareForMeWithLatestPreferences,
    retryForMeRefreshIfNeeded,
} from "../redux/actions/userActions";


import PreviewInfosLoading from "./PreviewInfosLoading";
import { EXPLORE_PREVIEW_INFO_PROPS } from "../constants/preview_info_constants";
import PreviewInfosVertical from "./PreviewInfosVertical";
import { mergeForMePreviews } from '../utils/forMePreviews';

export const ExploreLoggedIn = ({
    initialPreviews = [],
    // Redux.
    authenticated,
    forMePreviewsData,
    forMeLoadingCount,
    forMeLoadError = null,
    preferenceFeedRefreshState = 'idle',
    forMeDismissedInitialPreviewIds = {},
    loadForMePreviews,
    applyPreparedForMe,
    prepareForMeWithLatestPreferences,
    retryForMeRefreshIfNeeded,
}) => {
    const feedRef = useRef(null);
    const pendingApplyScroll = useRef(false);

    useEffect(() => {
        if (authenticated && !forMePreviewsData && !forMeLoadError && preferenceFeedRefreshState === 'idle') {
            loadForMePreviews();
        }
    }, [authenticated, forMePreviewsData, forMeLoadError, loadForMePreviews, preferenceFeedRefreshState]);

    useEffect(() => {
        if (!authenticated) return undefined;
        const retry = () => retryForMeRefreshIfNeeded();
        window.addEventListener('online', retry);
        return () => window.removeEventListener('online', retry);
    }, [authenticated, retryForMeRefreshIfNeeded]);

    useLayoutEffect(() => {
        if (!pendingApplyScroll.current || preferenceFeedRefreshState !== 'idle') return;
        pendingApplyScroll.current = false;
        // Wait for the replacement cards and banner removal to commit. Home's
        // measured section anchor also keeps the inline Ask and title visible.
        const target = feedRef.current?.closest('#explore') || feedRef.current;
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        target?.scrollIntoView?.({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    }, [forMePreviewsData, preferenceFeedRefreshState]);

    const pickedPreviews = Array.isArray(initialPreviews) ? initialPreviews.filter(preview => preview?.id && !forMeDismissedInitialPreviewIds[preview.id]) : [];
    const feedPreviews = Array.isArray(forMePreviewsData?.previews) ? forMePreviewsData.previews : [];
    const previews = mergeForMePreviews(pickedPreviews, feedPreviews);
    const feedLoading = forMeLoadingCount > 0 || (!forMePreviewsData && !forMeLoadError && preferenceFeedRefreshState === 'idle');
    // The signup examples are already ready. Keep them visible while the rest
    // of the feed loads, in the same cards and order the user was promised.

    const applyFeed = () => {
        pendingApplyScroll.current = true;
        if (!applyPreparedForMe()) pendingApplyScroll.current = false;
    };

    return (
        <div id='for-you-feed' ref={feedRef}>
            <FeedRefreshNotice state={preferenceFeedRefreshState} updatingLabel="Updating highlights…"
                onShow={applyFeed} onRetry={prepareForMeWithLatestPreferences} />
            {previews.length > 0 && <PreviewInfosVertical
                title="For you"
                queueDisplayedTopic
                previews={previews}
                previewInfoProps={EXPLORE_PREVIEW_INFO_PROPS}
            />}
            {forMeLoadError && !previews.length && preferenceFeedRefreshState === 'idle' && <div className='preferenceFeedRefreshBanner' role='status'>
                <span>{forMeLoadError}</span>
                <button type='button' onClick={retryForMeRefreshIfNeeded}>Try again</button>
            </div>}
            {feedLoading && !feedPreviews.length && <div
                className={`previewInfosVerticalLoadingList${previews.length ? ' previewInfosVerticalLoadingContinuation' : ''}`}
                role='status' aria-label={pickedPreviews.length ? 'Loading more highlights' : 'Loading your feed'}>
                <PreviewInfosLoading />
            </div>}
        </div>
    );
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
    forMePreviewsData: state.user.forMePreviewsData,
    forMeLoadingCount: state.user.forMeLoadingCount,
    forMeLoadError: state.user.forMeLoadError,
    preferenceFeedRefreshState: state.user.preferenceFeedRefreshState,
    forMeDismissedInitialPreviewIds: state.user.forMeDismissedInitialPreviewIds,
})

const mapActionsToProps = {
    loadForMePreviews,
    applyPreparedForMe,
    prepareForMeWithLatestPreferences,
    retryForMeRefreshIfNeeded,
}

export default connect(mapStateToProps, mapActionsToProps)(ExploreLoggedIn);
